/**
 * Vault Hire — Admin · DPA submissions queue + Compliance role assignments
 *
 * Owner / admin-only. Counter-sign incoming DPA copies and grant the
 * Compliance & Security Hub role to designated users (DPO, InfoSec,
 * Security Owner) on a per-user basis.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileSignature, ShieldCheck, CheckCircle2, AlertTriangle, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(d) { return d ? d.replace('T', ' ').slice(0, 19) : '—'; }

export default function AdminDPAQueuePage() {
  const [subs, setSubs] = useState(null);
  const [holders, setHolders] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [csNotes, setCsNotes] = useState({});

  const [emailLookup, setEmailLookup] = useState('');
  const [lookupUser, setLookupUser] = useState(null);
  const [lookupErr, setLookupErr] = useState('');

  const load = () => Promise.all([
    axios.get(`${API}/admin/dpa-submissions`),
    axios.get(`${API}/admin/compliance-hub/role-holders`),
  ]).then(([s, h]) => { setSubs(s.data.submissions); setHolders(h.data); })
    .catch((e) => setError(e.response?.data?.detail || 'Load failed.'));

  useEffect(() => { load(); }, []);

  const countersign = async (id) => {
    setBusy(id); setError('');
    try {
      await axios.post(`${API}/admin/dpa-submissions/${id}/countersign`, { notes: csNotes[id] || null });
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Counter-sign failed.'); }
    finally { setBusy(null); }
  };

  const findUser = async () => {
    setLookupErr(''); setLookupUser(null);
    if (!emailLookup.trim()) return;
    try {
      const { data } = await axios.get(`${API}/admin/users/search?email=${encodeURIComponent(emailLookup.trim())}`);
      if (data && data.user) setLookupUser(data.user);
      else setLookupErr('User not found.');
    } catch (e) { setLookupErr(e.response?.data?.detail || 'Search failed.'); }
  };

  const assignRole = async (userId, role) => {
    setBusy(`role-${userId}`); setError('');
    try {
      await axios.patch(`${API}/admin/users/${userId}/compliance-role`, { compliance_role: role });
      setEmailLookup(''); setLookupUser(null);
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Assign failed.'); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dpa-page">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-2">Admin · Compliance ops</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-2">DPA submissions &amp; Hub role assignments</h1>
        <p className="text-[var(--vh-text-secondary)] max-w-3xl leading-relaxed">
          Counter-sign incoming DPA copies and grant the Compliance &amp; Security Hub role (DPO / InfoSec / Security Owner) to customer users.
        </p>
      </header>

      {error && <div className="mb-4 p-3 rounded-lg bg-[var(--vh-accent)]/10 text-[var(--vh-accent)] text-sm inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}

      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <FileSignature className="w-5 h-5 text-[var(--vh-brand-primary)]" />
          <h2 className="font-display text-2xl text-[var(--vh-text-primary)]">DPA submissions</h2>
        </div>
        {!subs ? <div className="text-sm text-[var(--vh-text-secondary)]">Loading…</div> : (subs.length === 0 ? (
          <div className="bg-[var(--vh-bg-surface)] border border-dashed border-[var(--vh-border)] rounded-2xl p-10 text-center" data-testid="admin-dpa-empty">
            <p className="text-sm text-[var(--vh-text-secondary)]">No DPA submissions yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5" data-testid={`admin-dpa-row-${s.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--vh-text-primary)]">{s.organisation} — {s.signer_name}, {s.signer_title}</p>
                    <p className="text-xs text-[var(--vh-text-muted)] mt-1 font-mono">{s.signer_email} · {s.file_name} · {(s.file_size / 1024).toFixed(1)} KB</p>
                    <p className="text-[11px] text-[var(--vh-text-muted)] mt-1 font-mono">SHA-256 {s.file_sha256?.slice(0, 32)}…</p>
                    <p className="text-xs text-[var(--vh-text-secondary)] mt-2">Submitted {fmt(s.submitted_at)} {s.countersigned_at && <>· Counter-signed {fmt(s.countersigned_at)}</>}</p>
                    {s.notes && <p className="text-xs text-[var(--vh-text-secondary)] mt-1 italic">"{s.notes}"</p>}
                    {s.countersign_notes && <p className="text-xs text-[var(--vh-success)] mt-1 italic">VH note: "{s.countersign_notes}"</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em] ${s.status === 'countersigned' ? 'bg-[var(--vh-success)]/15 text-[var(--vh-success)]' : 'bg-[var(--vh-warning)]/30 text-[var(--vh-text-primary)]'}`}>{s.status}</span>
                </div>
                {s.status !== 'countersigned' && (
                  <div className="mt-4 flex gap-2 flex-wrap items-stretch">
                    <input
                      placeholder="Counter-sign notes (optional)"
                      value={csNotes[s.id] || ''}
                      onChange={(e) => setCsNotes({ ...csNotes, [s.id]: e.target.value })}
                      className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-[var(--vh-border)] bg-[var(--vh-bg-base)] text-sm text-[var(--vh-text-primary)]"
                      data-testid={`admin-dpa-notes-${s.id}`}
                    />
                    <button onClick={() => countersign(s.id)} disabled={busy === s.id} className="px-5 py-2 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid={`admin-dpa-countersign-${s.id}`}>
                      <CheckCircle2 className="w-4 h-4" /> {busy === s.id ? 'Signing…' : 'Counter-sign'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[var(--vh-brand-primary)]" />
          <h2 className="font-display text-2xl text-[var(--vh-text-primary)]">Hub role assignments</h2>
        </div>

        <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5 mb-5">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-3">Grant access to a user</p>
          <div className="flex gap-2 flex-wrap">
            <input
              type="email"
              placeholder="user@customer-organisation.com"
              value={emailLookup}
              onChange={(e) => setEmailLookup(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') findUser(); }}
              className="flex-1 min-w-[260px] px-3 py-2 rounded-lg border border-[var(--vh-border)] bg-[var(--vh-bg-base)] text-sm"
              data-testid="admin-hub-email-input"
            />
            <button onClick={findUser} className="px-5 py-2 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold inline-flex items-center gap-2" data-testid="admin-hub-find-btn"><UserPlus className="w-4 h-4" /> Find user</button>
          </div>
          {lookupErr && <p className="text-xs text-[var(--vh-accent)] mt-3">{lookupErr}</p>}
          {lookupUser && (
            <div className="mt-4 p-4 bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] rounded-xl flex justify-between items-center gap-3 flex-wrap" data-testid="admin-hub-user-found">
              <div className="text-sm">
                <p className="font-semibold text-[var(--vh-text-primary)]">{lookupUser.full_name || lookupUser.email}</p>
                <p className="text-xs text-[var(--vh-text-secondary)]">{lookupUser.email} · {lookupUser.company_name || 'no org'} · current: {lookupUser.compliance_role || 'none'}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['dpo', 'infosec', 'security_owner'].map((r) => (
                  <button key={r} onClick={() => assignRole(lookupUser.id, r)} disabled={busy === `role-${lookupUser.id}`} className="px-4 py-1.5 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-xs font-semibold disabled:opacity-50" data-testid={`admin-hub-assign-${r}`}>
                    Grant {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-2 overflow-x-auto">
          {!holders ? <div className="p-5 text-sm">Loading…</div> : (
            <table className="min-w-full text-sm">
              <thead className="text-[var(--vh-text-muted)] uppercase tracking-[0.12em] text-[10px]">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Organisation</th><th className="text-left p-3">Role</th><th className="text-left p-3">Granted</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--vh-border)]">
                {holders.role_holders.length === 0 && <tr><td colSpan="6" className="text-center p-6 text-[var(--vh-text-muted)]">No hub roles assigned yet.</td></tr>}
                {holders.role_holders.map((h) => (
                  <tr key={h.id} data-testid={`admin-hub-holder-${h.id}`}>
                    <td className="p-3 font-semibold text-[var(--vh-text-primary)]">{h.full_name || '—'}</td>
                    <td className="p-3 font-mono text-xs text-[var(--vh-text-secondary)]">{h.email}</td>
                    <td className="p-3 text-[var(--vh-text-secondary)]">{h.company_name || '—'}</td>
                    <td className="p-3"><span className="px-2.5 py-1 rounded-full bg-[var(--vh-accent)]/12 text-[var(--vh-accent)] text-[10px] uppercase font-semibold tracking-[0.12em]">{holders.role_labels?.[h.compliance_role] || h.compliance_role}</span></td>
                    <td className="p-3 text-xs text-[var(--vh-text-muted)] font-mono">{fmt(h.compliance_role_assigned_at)}</td>
                    <td className="p-3 text-right"><button onClick={() => assignRole(h.id, null)} disabled={busy === `role-${h.id}`} className="text-xs text-[var(--vh-accent)] hover:underline" data-testid={`admin-hub-revoke-${h.id}`}>Revoke</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
